import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Chip,
    useTheme,
    CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import { encodeId } from '../utils/idEncoder';

// --- TYPES ---
interface VehicleData {
    id: number;
    premium: boolean;
    views: number;
    price: string;
    model: string;
    specs: string;
    dealer: string;
    imageUrl: string;
}

const filters = ['All', 'Vans', 'Trucks', 'Low Mileage'];

// --- SUB-COMPONENTS ---

/**
 * Large Vehicle Card (Hero Deal)
 */
const LargeVehicleCard = ({ vehicle }: { vehicle: VehicleData }) => {
    return (
        <Paper
            component={RouterLink}
            to={`/vehicle-details/${encodeId(vehicle.id)}`}
            elevation={0}
            sx={{
                textDecoration: 'none',
                borderRadius: '16px',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)' }
            }}
        >
            {/* Background Image */}
            <Box sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `url(${vehicle.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 0
            }} />

            {/* Dark Gradient Overlay for text readability */}
            <Box sx={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.85) 100%)',
                zIndex: 1
            }} />

            <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2.5 }}>
                {/* Top Badges */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {vehicle.premium ? (
                        <Box sx={{ bgcolor: '#FFB800', color: '#fff', fontWeight: 900, fontSize: '0.65rem', px: 1.2, py: 0.4, borderRadius: '12px', letterSpacing: 0.5 }}>
                            PREMIUM
                        </Box>
                    ) : <Box />}
                    {/* <Box sx={{ bgcolor: '#fff', color: '#333', fontWeight: 800, fontSize: '0.75rem', px: 1.2, py: 0.4, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 0.5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <VisibilityIcon sx={{ fontSize: '1rem', color: '#FFB800' }} /> {vehicle.views}
                    </Box> */}
                </Box>

                {/* Bottom Content */}
                <Box>
                    <Typography sx={{ color: '#fff', fontSize: '2rem', fontWeight: 900, letterSpacing: -0.5, lineHeight: 1, mb: 0.5 }}>
                        {vehicle.price}
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600, mb: 1.5, lineHeight: 1.2, textTransform: 'uppercase' }}>
                        {vehicle.model}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.8} sx={{ mb: 2 }}>
                        {vehicle.specs.split(' | ').map((spec, index) => (
                            <Box key={index} sx={{ bgcolor: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', px: 1.2, py: 0.3, borderRadius: '6px' }}>
                                {spec}
                            </Box>
                        ))}
                    </Box>
                    <Typography sx={{ color: '#F97316', fontWeight: 900, letterSpacing: 0.5, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        {vehicle.dealer}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

/**
 * Small Vehicle Card (Stacked)
 */
const SmallVehicleCard = ({ vehicle }: { vehicle: VehicleData }) => {
    return (
        <Paper
            component={RouterLink}
            to={`/vehicle-details/${encodeId(vehicle.id)}`}
            elevation={0}
            sx={{
                textDecoration: 'none',
                borderRadius: '10px',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#fff',
                border: '1px solid #f2f2f2',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }
            }}
        >
            {/* Image Header */}
            <Box sx={{
                position: 'relative',
                height: '75%',
                backgroundImage: `url(${vehicle.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexShrink: 0
            }}>
                <Box sx={{ position: 'absolute', top: 4, left: 4, right: 4, zIndex: 1, display: 'flex', justifyContent: 'space-between' }}>
                    {vehicle.premium && (
                        <Box sx={{ bgcolor: '#FFB800', color: '#fff', fontWeight: 900, fontSize: '0.45rem', px: 0.5, py: 0.1, borderRadius: '3px' }}>
                            P
                        </Box>
                    )}
                    {/* <Box sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: '#333', fontWeight: 700, fontSize: '0.45rem', px: 0.5, py: 0.1, borderRadius: '3px', display: 'flex', alignItems: 'center', gap: 0.2 }}>
                        <VisibilityIcon sx={{ fontSize: '0.5rem', color: '#FFB800' }} /> {vehicle.views}
                    </Box> */}
                </Box>
            </Box>

            {/* Card Body */}
            <Box sx={{ p: 0.75, pt: 0.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                    <Typography sx={{ color: '#111', fontSize: '0.75rem', fontWeight: 800, lineHeight: 1, mb: 0.2 }}>
                        {vehicle.price}
                    </Typography>
                    <Typography sx={{
                        color: '#666',
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        lineHeight: 1.1,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {vehicle.model}
                    </Typography>
                </Box>

                <Typography sx={{
                    color: '#F97316',
                    fontWeight: 800,
                    fontSize: '0.45rem',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {vehicle.dealer}
                </Typography>
            </Box>
        </Paper>
    );
};

// --- MAIN COMPONENT ---
const HomepageBestDealsSection = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [vehicles, setVehicles] = useState<VehicleData[]>([]);

    useEffect(() => {
        const fetchDeals = async () => {
            setLoading(true);
            try {
                const payload: any = { pageNumber: 1, pageSize: 5, statusID: ['VPB'] };

                switch (filters[activeTab]) {
                    case 'Under $60K': payload.maxAskingPrice = 60000; break;
                    case 'Vans': payload.vehicleBodyTypeIds = [8]; break;
                    case 'Trucks': payload.vehicleBodyTypeIds = [5, 12, 19]; break;
                    case 'Low Mileage': payload.maxMileage = 60000; break;
                    default: break;
                }

                const response = await axiosInstance.post("/CustomerVehicle/VehicleGetListPublished", payload);
                const result = response.data as { responseCode: string; responseData?: any; };

                if (result.responseCode === "00" && result.responseData) {
                    const rawList = result.responseData.vehicleList || result.responseData.VehicleList || [];

                    const mappedData: VehicleData[] = rawList.map((item: any) => ({
                        id: item.vehicleId,
                        premium: item.racingAd || item.isPremium || false,
                        views: item.viewCount || Math.floor(Math.random() * 50),
                        price: item.askingPrice ? `$${item.askingPrice.toLocaleString()}` : 'POA',
                        model: item.vehicleModel || `${item.make || ''} ${item.model || ''}`.trim(),
                        specs: `${item.manufacturingYear || '-'} | ${item.mileage ? item.mileage.toLocaleString() + ' km' : '-'} | ${item.vehicleBodyType || '-'}`,
                        dealer: item.dealerCompany?.companyName || item.dealerName || 'Unknown Dealer',
                        imageUrl: item.vehicleImageUrl || 'https://via.placeholder.com/400x300?text=No+Image'
                    }));
                    setVehicles(mappedData.slice(0, 5));
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error("Failed to fetch best deals", error);
                setVehicles([]);
            } finally {
                setLoading(false);
            }
        };
        fetchDeals();
    }, [activeTab]);

    const smallVehicles = vehicles.slice(1);
    const pairedVehicles = [];
    for (let i = 0; i < smallVehicles.length; i += 2) {
        pairedVehicles.push(smallVehicles.slice(i, i + 2));
    }

    return (
        <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={900} sx={{ color: '#1a2a3a', letterSpacing: -0.5 }}>
                    Top Picks This Week
                </Typography>
                <Typography
                    onClick={() => navigate('/vehicle-listing')}
                    sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', cursor: 'pointer', '&:hover': { color: '#F97316' } }}
                >
                    View All ›
                </Typography>
            </Box>

            {/* Filters */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 3, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
                {filters.map((filter, index) => (
                    <Chip
                        key={filter}
                        label={filter}
                        onClick={() => setActiveTab(index)}
                        sx={{
                            fontWeight: 800, fontSize: '0.75rem', height: 32, px: 1,
                            bgcolor: activeTab === index ? '#F97316' : '#F3F4F6',
                            color: activeTab === index ? '#fff' : '#4B5563',
                            '&:hover': { bgcolor: activeTab === index ? '#EA580C' : '#E5E7EB' }
                        }}
                    />
                ))}
            </Stack>

            {/* Gallery Area */}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={350}>
                    <CircularProgress color="warning" />
                </Box>
            ) : vehicles.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={350} bgcolor="#f9f9f9" borderRadius={4}>
                    <Typography color="text.secondary" fontWeight={600}>No deals available.</Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row', // ✅ Always stay row-based
                        gap: { xs: 2, md: 2 },
                        width: '100%',
                        height: { xs: '340px', md: '360px' }, // ✅ Increased mobile height slightly so text doesn't cut off
                        overflowX: 'auto', // ✅ Let it scroll horizontally
                        overflowY: 'hidden',
                        scrollSnapType: 'x mandatory', // ✅ Makes swiping snap cleanly to the next card
                        pb: 1, // Padding so box shadows don't clip
                        '&::-webkit-scrollbar': { display: 'none' }, // ✅ Hides the scrollbar for a clean UI
                        scrollbarWidth: 'none',
                    }}
                >
                    {/* Large Card Column */}
                    <Box sx={{ 
                        flex: { xs: '0 0 85%', md: 2 }, // ✅ 85% width on mobile so you can peek at the next card
                        height: '100%',
                        scrollSnapAlign: 'start'
                    }}>
                        <LargeVehicleCard vehicle={vehicles[0]} />
                    </Box>

                    {/* Subsequent Columns: Stacked Small Cards */}
                    {pairedVehicles.map((pair, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 2, md: 2 },
                                flex: { xs: '0 0 65%', md: 1 }, // ✅ Takes up 65% width on mobile so they don't squish
                                height: '100%',
                                scrollSnapAlign: 'start'
                            }}
                        >
                            {pair.map(vehicle => (
                                <Box key={vehicle.id} sx={{ flex: 1, minHeight: 0 }}>
                                    <SmallVehicleCard vehicle={vehicle} />
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default HomepageBestDealsSection;